<template>
  <div class="app-container">
    <div class="am-toolbar">
      <el-input
        v-model="keyword"
        class="am-search"
        placeholder="搜索账号/昵称"
        clearable
        @keyup.enter.native="searchAgents"
        @clear="searchAgents"
      >
        <i slot="suffix" class="el-input__icon el-icon-search" @click="searchAgents"></i>
      </el-input>
      <el-button type="primary" icon="el-icon-plus" @click="openModal()">新增客服</el-button>
    </div>

    <el-table :data="agents" v-loading="loading" row-key="id">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column label="头像" width="60" align="center">
        <template slot-scope="{ row }">
          <el-image
            v-if="row.avatar"
            :src="row.avatar"
            fit="cover"
            style="width: 32px; height: 32px; border-radius: 50%"
          />
          <span v-else class="am-avatar-placeholder">?</span>
        </template>
      </el-table-column>
      <el-table-column prop="username" label="账号" width="120" />
      <el-table-column prop="nickname" label="昵称" width="120" />
      <el-table-column label="角色" width="80">
        <template slot-scope="{ row }">{{ RoleNameMap[row.role] || '未知' }}</template>
      </el-table-column>
      <el-table-column label="在线" width="80">
        <template slot-scope="{ row }">
          <el-tag :type="row.onlineStatus === 1 ? 'success' : row.onlineStatus === 2 ? 'warning' : 'info'">
            {{ row.onlineStatus === 1 ? '在线' : row.onlineStatus === 2 ? '忙碌' : '离线' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="80">
        <template slot-scope="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'danger'">{{ row.status === 1 ? '启用' : '停用' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="maxSessions" label="最大接待" width="90" />
      <el-table-column label="操作" width="200">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="openModal(row)">编辑</el-button>
          <el-button type="text" size="small" @click="handleToggle(row.id)">
            {{ row.status === 1 ? '停用' : '启用' }}
          </el-button>
          <el-button type="text" size="small" class="am-danger" @click="handleDelete(row.id)">删除</el-button>
        </template>
      </el-table-column>
      <div slot="empty">暂无客服</div>
    </el-table>
    <el-pagination
      class="am-pagination"
      layout="total, prev, pager, next"
      :current-page="page"
      :page-size="20"
      :total="total"
      @current-change="onPageChange"
    />

    <el-dialog :visible.sync="modalOpen" :title="editing ? '编辑客服' : '新增客服'" width="520px">
      <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
        <el-form-item label="账号" prop="username">
          <el-input v-model="form.username" :disabled="!!editing" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="form.password"
            type="password"
            :placeholder="editing ? '留空不修改' : '请输入密码'"
          />
        </el-form-item>
        <el-form-item label="昵称" prop="nickname">
          <el-input v-model="form.nickname" />
        </el-form-item>
        <el-form-item label="头像(URL)" prop="avatar">
          <el-input v-model="form.avatar" placeholder="可留空" />
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select v-model="form.role" class="full-width">
            <el-option v-for="(label, k) in RoleNameMap" :key="k" :label="label" :value="Number(k)" />
          </el-select>
        </el-form-item>
        <el-form-item label="最大接待数" prop="maxSessions">
          <el-input-number v-model="form.maxSessions" :min="1" :max="20" class="full-width" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="modalOpen = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listAgents, createAgent, updateAgent, toggleAgentStatus, deleteAgent } from '@/api/customerService/admin'

const RoleNameMap = {
  0: '客服',
  1: '主管',
  2: '管理员'
}

export default {
  name: 'AgentList',
  data () {
    return {
      RoleNameMap,
      agents: [],
      total: 0,
      page: 1,
      keyword: '',
      loading: false,
      modalOpen: false,
      editing: null,
      form: {
        username: '',
        password: '',
        nickname: '',
        avatar: '',
        role: 0,
        maxSessions: 5
      }
    }
  },
  computed: {
    rules () {
      return {
        username: this.editing ? [] : [this.required('请输入账号')],
        password: this.editing ? [] : [this.required('请输入密码')],
        nickname: [{ required: true, message: '请输入昵称', trigger: 'blur' }]
      }
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    required (message) {
      return { required: true, message, trigger: 'blur' }
    },
    async loadData () {
      this.loading = true
      try {
        const res = await listAgents({ keyword: this.keyword, pageNum: this.page, pageSize: 20 })
        this.agents = (res.result && res.result.list) || []
        const rawTotal = res.result && res.result.total
        this.total = Number(rawTotal) > 0 ? Number(rawTotal) : 0
      } catch (e) {
        this.$message.error('加载客服列表失败')
      }
      this.loading = false
    },
    searchAgents () {
      this.page = 1
      this.loadData()
    },
    onPageChange (p) {
      this.page = p
      this.loadData()
    },
    openModal (agent) {
      this.editing = agent || null
      this.form = {
        username: (agent && agent.username) || '',
        password: '',
        nickname: (agent && agent.nickname) || '',
        avatar: (agent && agent.avatar) || '',
        role: (agent && agent.role) || 0,
        maxSessions: (agent && agent.maxSessions) || 5
      }
      this.modalOpen = true
    },
    async handleSubmit () {
      const formEl = this.$refs.formRef
      if (!formEl) return
      await formEl.validate()
      if (this.editing) {
        const data = { ...this.form }
        if (!data.password) delete data.password
        await updateAgent(this.editing.id, data)
        this.$message.success('已更新')
      } else {
        await createAgent({ ...this.form })
        this.$message.success('已新增')
      }
      this.modalOpen = false
      this.loadData()
    },
    async handleToggle (id) {
      await toggleAgentStatus(id)
      this.$message.success('状态已切换')
      this.loadData()
    },
    async handleDelete (id) {
      try {
        await this.$confirm('确定删除该客服账号？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return // 用户取消
      }
      try {
        await deleteAgent(id)
        this.$message.success('已删除')
        this.loadData()
      } catch (err) {
        this.$message.error('删除失败：' + (err && err.message ? err.message : err))
      }
    }
  }
}
</script>

<style scoped>
.am-toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
}

.am-search {
  width: 300px;
}

.am-pagination {
  margin-top: 16px;
  text-align: right;
}

.am-danger {
  color: #f56c6c;
}

.am-avatar-placeholder {
  display: inline-block;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #c0c4cc;
  color: #fff;
  font-size: 14px;
  line-height: 32px;
  text-align: center;
}

.full-width {
  width: 100%;
}
</style>